import { FC, ReactElement } from 'react'

import { match } from 'ts-pattern'

import {
  Text,
  GreyGrey80,
  Primary13Regular,
  useNotifications,
  SimpleModal
} from '@sequencehq/core-components'

import { CustomerInfoReviewOutcome } from '../../types'

/**
 * Confirmation dialog for applying or dismissing the agent's proposed changes.
 * The caller passes the fully-formed outcome (the edited customer for confirm),
 * so this component only owns the copy and the submit/notify flow.
 */
export const ModalCustomerInfoReviewResponse: FC<{
  trigger: ReactElement
  outcome: CustomerInfoReviewOutcome
  customerName: string
  completeTask: (outcome: CustomerInfoReviewOutcome) => Promise<boolean>
}> = ({ trigger, outcome, customerName, completeTask }) => {
  const { displayNotification } = useNotifications()

  const { title, body, submitButtonText, submittingButtonText, isDestructive } =
    match(outcome.outcome)
      .with('CONFIRMED', () => ({
        title: 'Confirm changes',
        body: `Apply the proposed changes to ${customerName}'s billing details? The customer record will be updated.`,
        submitButtonText: 'Confirm changes',
        submittingButtonText: 'Applying changes...',
        isDestructive: false
      }))
      .with('DISMISSED', () => ({
        title: 'Dismiss proposed changes',
        body: `Dismiss the agent's proposed changes for ${customerName}? Nothing will be updated and the task will be closed.`,
        submitButtonText: 'Dismiss',
        submittingButtonText: 'Dismissing...',
        isDestructive: true
      }))
      .exhaustive()

  return (
    <SimpleModal
      variant="dialog"
      trigger={trigger}
      title={title}
      onSubmit={async () => {
        const succeeded = await completeTask(outcome)

        if (!succeeded) {
          displayNotification('Something went wrong. Please try again.', {
            type: 'error'
          })
          return false
        }

        displayNotification(
          match(outcome.outcome)
            .with('CONFIRMED', () => `Changes applied to ${customerName}`)
            .with('DISMISSED', () => `Proposed changes dismissed`)
            .exhaustive(),
          { type: 'success' }
        )

        return true
      }}
      submitButtonText={submitButtonText}
      submittingButtonText={submittingButtonText}
      isDestructive={isDestructive}
      dialogPosition="top"
    >
      <Text {...Primary13Regular} color={GreyGrey80}>
        {body}
      </Text>
    </SimpleModal>
  )
}
